import { getSourceAttribute } from '../utils/placeAttributes';

// Shared place-suitability ranking, used by both the activity detail sheet
// and the activity discovery view so they can never disagree about what the
// best place for an activity is.

// Weighted place-type suitability per activity. A type that's absent is
// never matched on type alone (a waterless town park is not a kayak spot).
// Weights are the base of a suitability score; distance is a tiebreaker,
// not the ranking — "further but bigger reward" should win.
export const ACTIVITY_POI_TYPES = {
  hiking: { hike: 3, trail: 2.5, waterfall: 1.8, park: 1.2, viewpoint: 1.2 },
  'trail-running': { trail: 3, hike: 2.5, park: 1.2 },
  running: { trail: 2.5, park: 1.5 },
  walking: { park: 2, trail: 1.5, viewpoint: 1 },
  'mountain-biking': { trail: 3, hike: 2 },
  fishing: { fishing: 3, water: 2.5, 'boat-launch': 2 },
  // Fly anglers want moving water; boat launches aren't the draw
  'fly-fishing': { water: 3, fishing: 2.5 },
  kayaking: { 'boat-launch': 3, water: 2.5, marina: 1.5, swimming: 1 },
  motorboating: { 'boat-launch': 3, marina: 2.5, water: 1.5 },
  paddleboarding: { 'boat-launch': 2.5, water: 2, swimming: 2 },
  // NO generic `park` fallback: a park is not evidence of swimming. Parks
  // qualify here only via a VERIFIED swimming_allowed (see
  // ACTIVITY_ATTRIBUTES) — otherwise a wooded village park with a carriage
  // trail ends up in "swimming spots near you", which is how trust dies.
  swimming: { swimming: 3, water: 1 },
  birdwatching: { park: 2, trail: 1.5, water: 1.5, viewpoint: 1 },
  'wildlife-watching': { park: 2, trail: 1.5, water: 1.5, viewpoint: 1 },
  picnic: { picnic: 3, park: 2, viewpoint: 1.2, waterfall: 1 },
  camping: { park: 2, trail: 1 },
  stargazing: { viewpoint: 2.5, park: 1 },
  'dog-walking': { park: 2, trail: 1.8 },
  'dog-park': { park: 2 },
  tennis: { tennis: 4 },
  basketball: { basketball: 4 },
  soccer: { soccer: 4 },
};

// OSM tags what's easy to draw (courts, park polygons), not what people seek
// — the region has 1 place tagged `swimming` but 68 our pipeline VERIFIED as
// swimmable. So a verified attribute is stronger evidence than an OSM type:
// it can qualify a place the type would have missed, and a verified "no"
// disqualifies it outright.
export const ACTIVITY_ATTRIBUTES = {
  swimming: { key: 'swimming_allowed', yes: ['yes'], no: ['no'] },
  fishing: { key: 'fishing', yes: ['yes'], no: ['no'] },
  'fly-fishing': { key: 'fishing', yes: ['yes'], no: ['no'] },
  'dog-walking': { key: 'dog_friendly', yes: ['yes', 'leashed'], no: ['no'] },
  'dog-park': { key: 'dog_friendly', yes: ['yes'], no: ['no'] },
  hiking: { key: 'difficulty', yes: ['easy', 'moderate', 'hard'] },
  'trail-running': { key: 'difficulty', yes: ['easy', 'moderate'] },
};

// Suitability signals beyond type: protected-area designations, terrain
// words in the name, and enrichment (a place we can say things about is a
// better recommendation than a mystery pin).
const REWARD_NAME = /mountain|ledge|summit|ridge|falls|gorge|kill\b|preserve|forest|point/i;

// "E-27" / "W-24" are DEC access codes — the data behind them is good but the
// name tells a user nothing, so they shouldn't headline a results list. Real
// named waters and trails rank first; codes fill in below.
export function isCrypticPlaceName(name) {
  const n = String(name || '').trim();
  if (/^[a-z]{1,2}-?\d+[a-z]?$/i.test(n)) return true; // E-27, W24, E-23C
  return (n.match(/[a-z]/gi) || []).length < 3;
}

export function scorePlace(place, weights, enrichmentRecord, activityId) {
  // Pending closure reports are not proof of closure, but should not be recommendations.
  if (['closed', 'restricted'].includes(enrichmentRecord?.attributes?.access_status?.value)) return -Infinity;
  const typeWeight = weights[place.type] ?? 0;

  // Verified attribute evidence for this activity, if we have any.
  const rule = ACTIVITY_ATTRIBUTES[activityId];
  const attrValue = rule
    ? String(enrichmentRecord?.attributes?.[rule.key]?.value ?? '').toLowerCase()
    : '';
  const supported = rule ? getSourceAttribute(enrichmentRecord, rule.key) : null;
  const verifiedYes = Boolean(supported && rule.yes.includes(attrValue));
  const verifiedNo = Boolean(rule && attrValue && rule.no?.includes(attrValue));

  // A verified "no" beats any type signal — never suggest a park for
  // swimming when the source says swimming is prohibited.
  if (verifiedNo) return -Infinity;
  // Qualify on EITHER a usable type or verified evidence.
  if (!typeWeight && !verifiedYes) return -Infinity;

  let score = typeWeight * 2;
  if (verifiedYes) score += 5; // verified beats a mere type match
  if (place.details?.designation) score += 1.25;
  if (REWARD_NAME.test(place.name)) score += 0.75;
  if (isCrypticPlaceName(place.name)) score -= 4; // unrecognizable name = poor headline
  if (enrichmentRecord) {
    score += 0.5;
    // Favor places with usable trip details, not a large set of low-confidence guesses.
    const usefulKeys = ['parking', 'restrooms', 'permit_or_fee', 'dog_friendly'];
    if (activityId === 'hiking') usefulKeys.push('difficulty');
    const usefulCount = usefulKeys.filter((key) => getSourceAttribute(enrichmentRecord, key)).length;
    score += Math.min(usefulCount, 4) * 0.75;
  }
  score -= Math.min(place.distance ?? 0, 40) * 0.12;
  return score;
}

/** True when this activity is about going somewhere (vs. at-home ones like gardening). */
export function hasPlaces(activityId) {
  return Boolean(ACTIVITY_POI_TYPES[activityId] || ACTIVITY_ATTRIBUTES[activityId]);
}

/** Best places for an activity, suitability-ranked. */
export function getRelevantPlaces(activityId, nearbyItems = [], enrichment = null, limit = 8) {
  const weights = ACTIVITY_POI_TYPES[activityId] ?? {};
  // An activity with no type weights can still match on verified attributes.
  if (!ACTIVITY_POI_TYPES[activityId] && !ACTIVITY_ATTRIBUTES[activityId]) return [];

  return nearbyItems
    .map((item) => ({
      item,
      score: scorePlace(item, weights, enrichment?.get(item.id), activityId),
    }))
    .filter(({ score }) => score > -Infinity)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ item }) => item);
}
